import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import StudentHeader from '@/components/student/StudentHeader';

import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  ClipboardList, Clock, CheckCircle, XCircle, Play, Award, Timer,
  FileText, Target, AlertCircle, Eye, BookOpen, RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID;

const StudentExamsPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('available');
  const [availableExams, setAvailableExams] = useState([]);
  const [myResults, setMyResults] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) {
      toast.error('Please login to view exams');
      navigate('/auth/login');
      return;
    }
    fetchData();
  }, [isAuthenticated]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch from multiple sources
      const allExams = [];
      const allResults = [];
      
      // 1. Online exams from /api/student/exams
      try {
        const examsRes = await axios.get(`${API}/student/exams`);
        if (examsRes.data?.length > 0) {
          allExams.push(...examsRes.data.map(e => ({
            ...e,
            exam_source: 'online'
          })));
        }
      } catch (e) {
        console.log('Online exams fetch failed:', e);
      }
      
      // 2. Get my admissions and create exam entries for confirmed courses
      try {
        const admissionsRes = await axios.get(`${API}/admissions/my-admissions`);
        const confirmedAdmissions = admissionsRes.data?.filter(a => 
          a.status === 'confirmed' || a.status === 'approved'
        ) || [];
        
        // Check dynamic exam attempts for each admission
        const dynamicAttemptsRes = await axios.get(`${API}/dynamic-exam/my-attempts`);
        const dynamicAttempts = dynamicAttemptsRes.data || [];
        
        for (const admission of confirmedAdmissions) {
          // Check if this course already has an online exam
          const hasOnlineExam = allExams.some(e => 
            e.course_id === admission.course_id || 
            e.course_name?.toLowerCase() === admission.course_name?.toLowerCase()
          );
          
          if (!hasOnlineExam) {
            // Get attempts for this course
            const courseAttempts = dynamicAttempts.filter(a => 
              a.course_id === admission.course_id || 
              a.course_name?.toLowerCase() === admission.course_name?.toLowerCase()
            );
            
            const completedAttempts = courseAttempts.filter(a => a.status === 'completed');
            const lastAttempt = completedAttempts[0] || courseAttempts[0];
            
            allExams.push({
              id: `admission-${admission.id}`,
              title: `${admission.course_name} Certification Exam`,
              course_id: admission.course_id,
              course_name: admission.course_name,
              description: `Complete this exam to earn your ${admission.training_mode || 'STT'} certificate`,
              time_per_question: 60,
              total_questions: 50,
              max_attempts: 3,
              passing_percentage: 40,
              is_batch_exam: false,
              is_published: true,
              is_active: true,
              my_attempts: completedAttempts.length,
              can_attempt: !lastAttempt || (lastAttempt.result !== 'pass' && completedAttempts.length < 3),
              last_attempt: lastAttempt ? {
                id: lastAttempt.id,
                result: lastAttempt.result,
                percentage: lastAttempt.percentage
              } : null,
              result_available: !!lastAttempt,
              exam_source: 'dynamic',
              admission_id: admission.id,
              training_mode: admission.training_mode
            });
          }
        }
      } catch (e) {
        console.log('Admissions fetch failed:', e);
      }
      
      // 3. Get exam results from multiple sources
      try {
        const resultsRes = await axios.get(`${API}/student/exams/results`);
        allResults.push(...(resultsRes.data || []));
      } catch (e) {
        // Endpoint may be unavailable for centers without batch exams enabled — non-fatal.
        if (process.env.NODE_ENV === 'development') {
          console.warn('Batch exam results unavailable:', e?.response?.status || e?.message);
        }
      }
      
      // 4. Add dynamic exam results
      try {
        const dynamicRes = await axios.get(`${API}/dynamic-exam/my-attempts`);
        const completedDynamic = dynamicRes.data?.filter(a => a.status === 'completed') || [];
        
        for (const attempt of completedDynamic) {
          // Avoid duplicates
          if (!allResults.some(r => r.id === attempt.id)) {
            allResults.push({
              id: attempt.id,
              exam_id: attempt.exam_id || `dynamic-${attempt.course_id}`,
              exam_title: `${attempt.course_name || attempt.course_id} Exam`,
              course_name: attempt.course_name || attempt.course_id,
              end_time: attempt.end_time,
              marks_obtained: attempt.marks_obtained,
              total_marks: attempt.total_marks,
              percentage: attempt.percentage,
              result: attempt.result,
              is_batch_exam: false,
              result_shown: true,
              source: 'dynamic'
            });
          }
        }
      } catch (e) {
        // Dynamic-exam attempts endpoint may not exist for all centers — non-fatal.
        if (process.env.NODE_ENV === 'development') {
          console.warn('Dynamic exam attempts unavailable:', e?.response?.status || e?.message);
        }
      }
      
      setAvailableExams(allExams);
      setMyResults(allResults);
    } catch (error) {
      console.error('Failed to fetch exams:', error);
    } finally {
      setLoading(false);
    }
  };

  const startExam = (exam) => {
    if (window.confirm('Are you ready to start the exam? Once started, the timer will begin.')) {
      if (exam.exam_source === 'dynamic' || exam.id?.startsWith('admission-')) {
        // Dynamic exam - navigate to dynamic exam page
        const courseSlug = exam.course_name
          ?.toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '');
        navigate(`/student/dynamic-exam?course=${courseSlug}&mode=${exam.training_mode || 'STT'}`);
      } else {
        // Online exam
        navigate(`/student/exam/${exam.id}`);
      }
    }
  };

  const viewResult = async (attemptId) => {
    try {
      const res = await axios.get(`${API}/student/exams/results/${attemptId}`);
      // Open in new page or modal - for now navigate
      navigate(`/student/exam-result/${attemptId}`);
    } catch (error) {
      if (error.response?.data?.detail === 'Results not yet released') {
        toast.info('Results will be released after the exam ends for all students');
      } else {
        toast.error('Failed to load result details');
      }
    }
  };

  // Reassessment Handler
  const handleReassessment = async (result) => {
    try {
      // Create Razorpay order for reassessment
      const orderRes = await axios.post(`${API}/reassessment/create-order?attempt_id=${result.id}`);
      const orderData = orderRes.data;
      
      // Load Razorpay script if not loaded
      if (!window.Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        document.body.appendChild(script);
        await new Promise(resolve => script.onload = resolve);
      }
      
      const options = {
        key: orderData.key_id || RAZORPAY_KEY,
        amount: orderData.amount * 100,
        currency: orderData.currency,
        name: 'Iqra Skills',
        description: orderData.description,
        order_id: orderData.order_id,
        prefill: {
          name: orderData.user_name,
          email: orderData.user_email
        },
        theme: {
          color: '#4F46E5'
        },
        handler: async function (response) {
          try {
            // Verify payment
            await axios.post(`${API}/reassessment/verify-payment`, null, {
              params: {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              }
            });
            
            toast.success('Payment successful! You can now retake the exam.');
            fetchData(); // Refresh exam list
          } catch (err) {
            toast.error('Payment verification failed');
          }
        }
      };
      
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error('Reassessment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate reassessment');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <StudentHeader />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
        
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <StudentHeader />
      
      <div className="max-w-6xl mx-auto py-8 px-4">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-800">Online Exams</h1>
          <p className="text-slate-600">View available exams and your results</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="available" className="flex items-center gap-2">
              <ClipboardList className="h-4 w-4" />
              Available Exams ({availableExams.filter(e => e.can_attempt).length})
            </TabsTrigger>
            <TabsTrigger value="results" className="flex items-center gap-2">
              <Award className="h-4 w-4" />
              My Results ({myResults.length})
            </TabsTrigger>
          </TabsList>

          {/* Available Exams Tab */}
          <TabsContent value="available" className="space-y-4">
            {availableExams.length > 0 ? (
              <div className="grid gap-4">
                {availableExams.map((exam) => (
                  <div 
                    key={exam.id} 
                    className="bg-white rounded-xl shadow-sm p-6 border border-slate-200"
                    data-testid={`exam-card-${exam.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-slate-800">{exam.title}</h3>
                          {exam.is_batch_exam && (
                            <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                              Batch Exam
                            </span>
                          )}
                        </div>
                        <p className="text-slate-600 text-sm mb-4">{exam.description}</p>
                        
                        <div className="flex flex-wrap gap-4 text-sm">
                          <div className="flex items-center gap-1 text-slate-500">
                            <BookOpen className="h-4 w-4" />
                            {exam.course_name}
                          </div>
                          <div className="flex items-center gap-1 text-slate-500">
                            <FileText className="h-4 w-4" />
                            {exam.total_questions} Questions
                          </div>
                          <div className="flex items-center gap-1 text-slate-500">
                            <Timer className="h-4 w-4" />
                            {exam.time_per_question}s per question
                          </div>
                          <div className="flex items-center gap-1 text-slate-500">
                            <Target className="h-4 w-4" />
                            Pass: {exam.passing_percentage}%
                          </div>
                        </div>
                        
                        {exam.my_attempts > 0 && (
                          <div className="mt-3 text-sm">
                            <span className="text-slate-500">
                              Attempts: {exam.my_attempts} / {exam.max_attempts}
                            </span>
                            {exam.last_attempt && (
                              <span className={`ml-3 px-2 py-1 rounded-full text-xs font-medium ${
                                exam.last_attempt.result === 'pass' 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                Last: {exam.last_attempt.percentage?.toFixed(1)}% 
                                ({exam.last_attempt.result?.toUpperCase()})
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <div className="ml-4">
                        {exam.can_attempt ? (
                          <Button 
                            onClick={() => startExam(exam)}
                            className="bg-blue-600 hover:bg-blue-700"
                            data-testid={`start-exam-btn-${exam.id}`}
                          >
                            <Play className="h-4 w-4 mr-2" />
                            {exam.my_attempts > 0 ? 'Retry Exam' : 'Start Exam'}
                          </Button>
                        ) : (
                          <div className="text-center">
                            <Button disabled variant="outline" className="mb-2">
                              <AlertCircle className="h-4 w-4 mr-2" />
                              Max Attempts Reached
                            </Button>
                            {exam.result_available && (
                              <Button 
                                variant="link" 
                                size="sm"
                                onClick={() => viewResult(exam.last_attempt?.id)}
                              >
                                View Result
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl p-12 text-center">
                <ClipboardList className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium text-slate-700 mb-2">No Exams Available</h3>
                <p className="text-slate-500">
                  Exams will appear here once you're enrolled in a course with published exams.
                </p>
              </div>
            )}
          </TabsContent>

          {/* Results Tab */}
          <TabsContent value="results" className="space-y-4">
            {myResults.length > 0 ? (
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-slate-900">Exam</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-slate-900">Course</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-slate-900">Date</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-slate-900">Score</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-slate-900">Result</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-slate-900">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {myResults.map((result) => (
                      <tr key={result.id} className="hover:bg-slate-50" data-testid={`result-row-${result.id}`}>
                        <td className="px-6 py-4">
                          <p className="font-medium text-slate-800">{result.exam_title}</p>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">{result.course_name}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {result.end_time ? new Date(result.end_time).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-6 py-4">
                          {result.is_batch_exam && !result.result_shown ? (
                            <span className="text-slate-500 text-sm">Pending</span>
                          ) : (
                            <div>
                              <span className="font-medium">{result.marks_obtained}</span>
                              <span className="text-slate-400">/{result.total_marks}</span>
                              <span className={`ml-2 text-sm ${
                                result.percentage >= 40 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                ({result.percentage?.toFixed(1)}%)
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {result.is_batch_exam && !result.result_shown ? (
                            <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                              AWAITING
                            </span>
                          ) : (
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              result.result === 'pass' 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {result.result?.toUpperCase()}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {(!result.is_batch_exam || result.result_shown) && (
                            <div className="flex gap-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => navigate(`/student/exam-result/${result.id}`)}
                                data-testid={`view-detail-btn-${result.id}`}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                Details
                              </Button>
                              {result.result === 'fail' && (
                                <Button 
                                  size="sm"
                                  onClick={() => handleReassessment(result)}
                                  className="bg-orange-600 hover:bg-orange-700"
                                  data-testid={`reassess-btn-${result.id}`}
                                >
                                  <RefreshCw className="h-4 w-4 mr-1" />
                                  Reassess (₹500)
                                </Button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bg-white rounded-xl p-12 text-center">
                <Award className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium text-slate-700 mb-2">No Results Yet</h3>
                <p className="text-slate-500">
                  Your exam results will appear here after you complete an exam.
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      
    </div>
  );
};

export default StudentExamsPage;
